import { Wishlist } from '@shared/types/wishlist/Wishlist';

/**
 * EWM-892 — `wishlist/removeLineItems` answers in one of two shapes.
 *
 * Every list committed (which is every request the live callers make, since
 * they all send a single list) → the bare `Wishlist[]` this action has always
 * returned. Some lists committed and some did not → `{ updated, failed }`, so
 * the caller can tell which is which instead of being told the whole request
 * failed when part of it did not.
 *
 * Total failure is not represented here: it comes back as an error response,
 * so `data` is undefined and both arrays are empty.
 */
export interface RemoveLineItemsResult {
  updated: Wishlist[];
  failed: { wishlistId: string }[];
}

export type RemoveLineItemsResponse =
  Wishlist[] | { updated?: Wishlist[]; failed?: { wishlistId: string }[] } | undefined;

/**
 * Normalise either shape into one the callers can read unconditionally.
 *
 * Deliberately tolerant of `undefined`: the action's error path returns no
 * body, and callers already treat "no data" as failure.
 */
export const toRemoveLineItemsResult = (data: RemoveLineItemsResponse): RemoveLineItemsResult => {
  if (Array.isArray(data)) {
    return { updated: data, failed: [] };
  }

  return { updated: data?.updated ?? [], failed: data?.failed ?? [] };
};

/**
 * True when at least one list committed.
 *
 * This is the predicate the callers actually want: a partial removal DID remove
 * the items it names, so reporting it as an outright failure is the bug this
 * ticket exists to fix.
 */
export const didRemoveAny = (data: RemoveLineItemsResponse): boolean =>
  toRemoveLineItemsResult(data).updated.length > 0;
